import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, MapPin, Mail, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

// Logo Component - Avyan Orthopaedics "Ao" Logo
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_precision-joints-1/artifacts/z2ferz2k_IMG_20260326_153525.png" 
    alt="Avyan Orthopaedics" 
    className="h-10 w-auto"
  />
);

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/#about', label: 'About' },
    { href: '/#specialties', label: 'Specialties' },
    { href: '/#experience', label: 'Experience' },
    { href: '/blog', label: 'Blog' },
    { href: '/#contact', label: 'Contact' }
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href.replace('/', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href.replace('/', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(href);
    }
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-[#E8EEF5]/20'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Logo />
            <div className="hidden sm:block">
              <div className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1E2A4A]">
                Avyan Orthopaedics
              </div>
              <div className="text-[10px] text-[#7A8FA0] tracking-wide">
                317, Maple Trade Centre, Memnagar, Ahmedabad
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-[11px] tracking-[0.12em] uppercase text-[#7A8FA0] hover:text-[#1E2A4A] transition-colors font-medium"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link to="/portal" data-testid="portal-link">
                  <Button variant="ghost" className="text-[11px] tracking-wider uppercase text-[#7A8FA0]">
                    My Portal
                  </Button>
                </Link>
                <Button
                  onClick={logout}
                  variant="outline"
                  className="text-[11px] tracking-wider uppercase"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button variant="ghost" className="text-[11px] tracking-wider uppercase text-[#7A8FA0]">
                  Login
                </Button>
              </Link>
            )}
            
            <Link to="/book-appointment" data-testid="book-appointment-nav">
              <Button className="bg-[#1E2A4A] hover:bg-[#4A6FA5] text-[#E8EEF5] px-6 py-2 rounded text-[11px] tracking-[0.2em] uppercase font-medium">
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1E2A4A]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1E2A4A]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-[#E8EEF5]/20"
          >
            <div className="container-custom py-4 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full text-left py-2 text-sm text-[#1E2A4A] hover:text-[#4A6FA5] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 border-t border-[#E8EEF5]/20 space-y-3">
                {isAuthenticated ? (
                  <>
                    <Link to="/portal" className="block">
                      <Button variant="outline" className="w-full">My Portal</Button>
                    </Link>
                    <Button onClick={logout} variant="ghost" className="w-full">Logout</Button>
                  </>
                ) : (
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full">Login / Register</Button>
                  </Link>
                )}
                <Link to="/book-appointment" className="block">
                  <Button className="w-full bg-[#1E2A4A] hover:bg-[#4A6FA5] text-white">
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1E2A4A] text-white" data-testid="footer">
      <div className="container-custom py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_precision-joints-1/artifacts/z2ferz2k_IMG_20260326_153525.png" 
                alt="Avyan Orthopaedics" 
                className="h-8 w-auto brightness-0 invert opacity-80"
              />
              <div>
                <div className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#E8EEF5]">
                  Avyan Orthopaedics
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#E8EEF5]/30">
                  Joint Replacement - Arthroscopy - Sports Injury
                </div>
              </div>
            </div>
            <p className="text-sm text-[#E8EEF5]/50 max-w-md leading-relaxed">
              Dr. Drumil D Majmundar brings over 9 years of experience and 10,000+ successful surgeries, 
              combining world-class training from Germany with compassionate patient care.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-medium text-[#E8EEF5] mb-4 tracking-wider uppercase">Quick Links</h4>
            <ul className="space-y-2">
              {['About', 'Specialties', 'Experience', 'Blog', 'Contact'].map((link) => (
                <li key={link}>
                  <Link
                    to={link === 'Blog' ? '/blog' : `/#${link.toLowerCase()}`}
                    className="text-sm text-[#E8EEF5]/40 hover:text-[#E8EEF5] transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-medium text-[#E8EEF5] mb-4 tracking-wider uppercase">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-[#E8EEF5]/40">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#4A6FA5]" />
                <span>3rd Floor, 317 Maple Trade Centre, Memnagar, Ahmedabad 380052</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#E8EEF5]/40">
                <Phone className="w-4 h-4 flex-shrink-0 text-[#4A6FA5]" />
                <a href="tel:+917043543985" className="hover:text-[#E8EEF5]">+91-7043543985</a>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#E8EEF5]/40">
                <Mail className="w-4 h-4 flex-shrink-0 text-[#4A6FA5]" />
                <a href="mailto:drumil24@gmail.com" className="hover:text-[#E8EEF5]">drumil24@gmail.com</a>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#E8EEF5]/40">
                <Clock className="w-4 h-4 flex-shrink-0 text-[#4A6FA5]" />
                <span>Mon - Sat: 10:00 AM - 7:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#E8EEF5]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#E8EEF5]/20">
            © {currentYear} Avyan Orthopaedics - Dr. Drumil D Majmundar, MS Orthopaedics. All rights reserved.
          </p>
          <div className="flex gap-4 text-xs text-[#E8EEF5]/20">
            <a href="#" className="hover:text-[#E8EEF5]">Privacy Policy</a>
            <a href="#" className="hover:text-[#E8EEF5]">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/917043543985?text=Hello%20Dr.%20Majmundar%2C%20I%20would%20like%20to%20book%20a%20consultation%20at%20Avyan%20Orthopaedics."
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 bg-[#1E2A4A] hover:bg-[#4A6FA5] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 border-2 border-[#4A6FA5]/35 group"
    aria-label="Chat on WhatsApp"
    data-testid="whatsapp-button"
  >
    <div className="absolute inset-[-4px] rounded-full border-2 border-[#4A6FA5] wa-pulse" />
    <svg width="26" height="26" viewBox="0 0 32 32" fill="white">
      <path d="M16 0C7.164 0 0 7.163 0 16c0 2.833.737 5.494 2.027 7.807L.069 31.07a1 1 0 001.212 1.212l7.264-1.957A15.936 15.936 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm8.322 22.293c-.346.974-1.719 1.782-2.813 2.017-.748.161-1.724.289-5.009-1.077-4.199-1.726-6.907-5.986-7.115-6.263-.201-.277-1.683-2.239-1.683-4.269 0-2.031 1.065-3.026 1.443-3.442.378-.416.824-.52 1.099-.52.274 0 .549.003.789.014.253.012.592-.096.927.707.346.826 1.173 2.856 1.275 3.064.103.207.172.449.034.725-.138.277-.207.449-.414.691-.207.243-.435.542-.621.728-.207.207-.422.431-.181.845.241.414 1.071 1.767 2.3 2.862 1.582 1.409 2.916 1.845 3.33 2.052.414.207.656.172.897-.104.241-.276 1.032-1.204 1.308-1.618.276-.414.552-.345.931-.207.379.138 2.403 1.134 2.817 1.341.414.207.69.311.793.483.103.173.103.999-.243 1.97z"/>
    </svg>
    <span className="absolute right-16 top-1/2 -translate-y-1/2 bg-[#1E2A4A] text-[#E8EEF5] text-xs font-medium whitespace-nowrap px-3 py-2 rounded opacity-0 group-hover:opacity-100 transition-opacity border border-[#4A6FA5]/20">
      Book via WhatsApp
    </span>
  </a>
);

// Main Layout Component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF8]">
      <Navigation />
      <main className="flex-1 pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
